import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BookMarked, Heart, HandCoins, Sparkles, Globe, BookOpen, MessageSquare, Calendar, Book } from 'lucide-react';

export default function Landing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'hr' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const features = [
    {
      icon: BookMarked,
      title: t('prayer_tracker'),
      description: t('feat_prayer_desc'),
    },
    {
      icon: HandCoins,
      title: t('sadaqah_tracker'),
      description: t('feat_sadaqah_desc'),
    },
    {
      icon: Heart,
      title: t('good_deeds_log'),
      description: t('feat_good_deeds_desc'),
    },
    {
      icon: Sparkles,
      title: t('dhikr_counter'),
      description: t('feat_dhikr_desc'),
    },
    {
      icon: BookOpen,
      title: t('quran'),
      description: t('feat_quran_desc'),
    },
    {
      icon: Book,
      title: t('hadith'),
      description: t('feat_hadith_desc'),
    },
    {
      icon: MessageSquare,
      title: t('journal'),
      description: t('feat_journal_desc'),
    },
    {
      icon: Calendar,
      title: t('planner'),
      description: t('feat_planner_desc'),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F4] grain-texture">
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-heading font-semibold text-[#292524] tracking-tight">
              {t('app_name')}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              data-testid="landing-language-toggle"
              className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-white/50 transition-all"
            >
              <Globe className="w-4 h-4 text-[#57534E]" />
              <span className="text-sm font-medium text-[#57534E]">
                {i18n.language.toUpperCase()}
              </span>
            </button>
            <button
              onClick={() => navigate('/login')}
              data-testid="landing-login-button"
              className="px-6 py-2 rounded-full bg-white text-[#292524] hover:bg-white/90 transition-all font-medium shadow-sm"
            >
              {t('login')}
            </button>
          </div>
        </div>
      </header>

      <section className="relative pt-32 pb-20 px-6">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1758915388675-8eec5a7eab7f)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-[#292524] mb-6 tracking-tight">
            {t('your_spiritual_journey')}
          </h2>
          <p className="text-lg sm:text-xl text-[#57534E] mb-8 font-body max-w-2xl mx-auto">
            {t('track_organize_grow')}
          </p>
          <button
            onClick={() => navigate('/register')}
            data-testid="hero-get-started-button"
            className="px-8 py-3 bg-primary text-white rounded-full font-medium text-lg hover:bg-primary/90 transition-all shadow-md active:scale-95"
          >
            {t('get_started')}
          </button>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-6 hover:shadow-md transition-all duration-300"
              >
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-[#292524] mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#57534E] font-body">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-12">
          <h3 className="text-3xl font-heading font-bold text-[#292524] mb-4">
            {t('tagline')}
          </h3>
          <p className="text-lg text-[#57534E] mb-8 font-body">
            {t('open_source_multilingual')}
          </p>
          <button
            onClick={() => navigate('/register')}
            data-testid="cta-get-started-button"
            className="px-8 py-3 bg-primary text-white rounded-full font-medium text-lg hover:bg-primary/90 transition-all shadow-md active:scale-95"
          >
            {t('get_started')}
          </button>
        </div>
      </section>

      {/* Footer with Legal Links */}
      <footer className="py-8 px-6 border-t border-stone-200 bg-white/50">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-stone-500">
            © 2026 Bereket. {i18n.language === 'hr' ? 'Sva prava pridržana.' : 'All rights reserved.'}
          </p>
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/privacy')}
              className="text-sm text-stone-600 hover:text-[#6B7B3F] transition-colors"
            >
              {i18n.language === 'hr' ? 'Politika privatnosti' : 'Privacy Policy'}
            </button>
            <button
              onClick={() => navigate('/terms')}
              className="text-sm text-stone-600 hover:text-[#6B7B3F] transition-colors"
            >
              {i18n.language === 'hr' ? 'Uvjeti korištenja' : 'Terms of Service'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
